import { useReducer, useEffect, useMemo, useState, useCallback } from 'react';
import { GAME_STATUS, ACTIONS, UI_TEXT, LADDER_GAME_CONFIG } from './utils/constants.js';
import { ladderReducer, createInitialLadderState } from './utils/ladderReducer.js';
import {
  fetchLadderStatus,
  startLadderGame,
  resolveWinnerArrayIndex,
} from './utils/ladderApi.js';
import LadderCanvas from './LadderCanvas.jsx';
import LadderResult from './LadderResult.jsx';
import './LadderGame.css';

/**
 * LadderGame - Canvas-based ladder climbing game component
 */
export default function LadderGame({
  language = 'ko',
  token,
  customerId,
  campaignName = 'worldcup-ladder-2026',
  loginUrl,
  loginToPlay,
  ladderPlayerImgs,
  ladderBg,
  ladderPointerImg,
  ladderPrizeImg,
  testToken,
}) {
  const config = LADDER_GAME_CONFIG;
  const [state, dispatch] = useReducer(ladderReducer, config, createInitialLadderState);
  const [canPlay, setCanPlay] = useState(true);
  const [gameRewards, setGameRewards] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isStartingGame, setIsStartingGame] = useState(false);

  const text = UI_TEXT[language] || UI_TEXT.ko;
  const hasToken = Boolean(token && token.trim() !== '');

  const assets = useMemo(() => {
    if (!ladderPlayerImgs && !ladderBg && !ladderPointerImg && !ladderPrizeImg) {
      return null;
    }

    return {
      playerImgs: ladderPlayerImgs,
      pointerImg: ladderPointerImg,
      prizeImg: ladderPrizeImg,
    };
  }, [ladderPlayerImgs, ladderBg, ladderPointerImg, ladderPrizeImg]);

  const handleLogin = useCallback(() => {
    if (loginUrl) {
      window.location.href = loginUrl;
      return;
    }
    window.location.href = `${window.Shopify?.routes?.root || '/'}account/login`;
  }, [loginUrl]);

  const loadPlayStatus = useCallback(async () => {
    if (!hasToken) {
      setCanPlay(true);
      setGameRewards(null);
      setIsLoadingStatus(false);
      return;
    }

    setIsLoadingStatus(true);
    try {
      const data = await fetchLadderStatus(token, campaignName, language, testToken);
      setCanPlay(data.canPlay);
      setGameRewards(data.rewards);
    } catch (error) {
      console.error('Failed to load ladder status:', error);
      setCanPlay(false);
      setGameRewards(null);
    } finally {
      setIsLoadingStatus(false);
    }
  }, [hasToken, token, campaignName, language, testToken]);

  const handleStartGame = useCallback(async () => {
    if (!hasToken || !canPlay || isStartingGame || !gameRewards?.length) return;
    if (state.selectedStartIndex === null || state.status !== GAME_STATUS.SELECTING) return;

    setIsStartingGame(true);
    try {
      const { play } = await startLadderGame(token, campaignName, language, customerId, testToken);
      const winnerIndex = resolveWinnerArrayIndex(gameRewards, play.rewardIndex);

      setCanPlay(false);

      dispatch({
        type: ACTIONS.START_GAME,
        payload: { rewards: gameRewards, winnerIndex },
      });
    } catch (error) {
      console.error('Failed to start ladder game:', error);

      if (error.code === 'Already participated today') {
        setCanPlay(false);
      }
    } finally {
      setIsStartingGame(false);
    }
  }, [
    hasToken,
    canPlay,
    isStartingGame,
    gameRewards,
    state.selectedStartIndex,
    state.status,
    token,
    campaignName,
    language,
    customerId,
    testToken,
  ]);

  useEffect(() => {
    if (state.status === GAME_STATUS.IDLE) {
      dispatch({ type: ACTIONS.INITIALIZE_GAME });
    }
  }, []);

  useEffect(() => {
    loadPlayStatus();
  }, [loadPlayStatus]);

  useEffect(() => {
    if (state.status === GAME_STATUS.SHUFFLING) {
      const timer = setTimeout(() => {
        dispatch({ type: ACTIONS.COMPLETE_SHUFFLE });
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [state.status]);

  if (state.status === GAME_STATUS.IDLE) {
    return (
      <div className="ladder-game">
        <div className="ladder-game__loading">{text.loading}</div>
      </div>
    );
  }

  return (
    <div className="ladder-game">
      <div className="ladder-game__play">
        <LadderCanvas
          state={state}
          dispatch={dispatch}
          config={config}
          language={language}
          assets={assets}
          gameRewards={gameRewards}
          hasToken={hasToken}
          canPlay={canPlay}
          isLoadingStatus={isLoadingStatus}
          isStartingGame={isStartingGame}
          loginToPlay={loginToPlay}
          campaignName={campaignName}
          onLogin={handleLogin}
          onStartGame={handleStartGame}
          bg={ladderBg}
        />
      </div>

      {state.status === GAME_STATUS.RESULT && (
        <LadderResult
          resultIndex={state.resultIndex}
          shuffledRewards={state.shuffledRewards}
          rewards={state.rewards}
          prizeImg={ladderPrizeImg}
          onRestart={() => dispatch({ type: ACTIONS.RESTART_GAME })}
          language={language}
        />
      )}
    </div>
  );
}